import { clone, find as _find } from 'lodash';
import { generate } from 'short-uuid';
import {
  EPermissionType,
  IGraphData,
  ILink,
  IPermission,
  IPrivilege,
  IResource,
  IResourcePermission,
  IResourcesByPermission,
  ISanitized,
  TAction,
  TById,
  TRawData,
  TRole,
  TRolesDict,
  TSanitizedPrivilege,
} from './types';
import { validateName } from '@qodly/shared';

const TYPES = [
  EPermissionType.DATASTORE,
  EPermissionType.DATACLASS,
  EPermissionType.ATTRIBUTE,
  EPermissionType.SINGLETON_METHOD,
  EPermissionType.SINGLETON,
  EPermissionType.METHOD,
];

const TypeNamesMapping = (type: EPermissionType) => {
  return [
    EPermissionType.DS_METHOD,
    EPermissionType.ENTITY_METHOD,
    EPermissionType.DATACLASS_METHOD,
    EPermissionType.ENTITY_SEL_METHOD,
  ].includes(type)
    ? EPermissionType.METHOD
    : type;
};

const GUEST = 'guest';

const PERMISSIONS: {
  key: TAction;
  label: string;
  canApplyTo: EPermissionType[];
}[] = [
  {
    key: 'read',
    label: 'Read',
    canApplyTo: [
      EPermissionType.DATASTORE,
      EPermissionType.DATACLASS,
      EPermissionType.ATTRIBUTE,
    ],
  },
  {
    key: 'create',
    label: 'Create',
    canApplyTo: [
      EPermissionType.DATASTORE,
      EPermissionType.DATACLASS,
      EPermissionType.ATTRIBUTE,
    ],
  },
  {
    key: 'update',
    label: 'Update',
    canApplyTo: [
      EPermissionType.DATASTORE,
      EPermissionType.DATACLASS,
      EPermissionType.ATTRIBUTE,
    ],
  },
  {
    key: 'drop',
    label: 'Delete',
    canApplyTo: [
      EPermissionType.DATASTORE,
      EPermissionType.DATACLASS,
      EPermissionType.ATTRIBUTE,
    ],
  },
  {
    key: 'execute',
    label: 'Execute',
    canApplyTo: [
      EPermissionType.DATASTORE,
      EPermissionType.DATACLASS,
      EPermissionType.METHOD,
      EPermissionType.DS_METHOD,
      EPermissionType.DATACLASS_METHOD,
      EPermissionType.ENTITY_METHOD,
      EPermissionType.ENTITY_SEL_METHOD,
      EPermissionType.SINGLETON,
      EPermissionType.SINGLETON_METHOD,
    ],
  },
  {
    key: 'promote',
    label: 'Promote',
    canApplyTo: [
      // EPermissionType.DATASTORE,
      // EPermissionType.DATACLASS,
      EPermissionType.METHOD,
      EPermissionType.DS_METHOD,
      EPermissionType.DATACLASS_METHOD,
      EPermissionType.ENTITY_METHOD,
      EPermissionType.ENTITY_SEL_METHOD,
      EPermissionType.SINGLETON_METHOD,
    ],
  },
];

/**
 * pushes uniquely list of items
 * @param array input array
 * @param args list of elements to add to the array
 * @returns an array containing unique fields
 */
export function uniquePush<T = string>(array: T[], ...args: T[]): T[] {
  return Array.from(new Set([...array, ...args]));
}

/**
 * Sanitize a privileges array
 * @param data unsanitized data
 * @returns sanitized data
 */
export function sanitizePrivileges(data: IPrivilege[]): TById {
  const result: TById = {};
  const byName = new Map<string, string>();

  data.forEach((item) => {
    const id = item.id || generate();
    /**
     * Populate the "result" set with sets instead of arrays
     * to ensure the unicity of keys
     **/
    result[id] = {
      ...item,
      id,
      parents: [],
      includes: uniquePush(item.includes || []),
      resources: [],
    };
    /**
     * Populate the byName dictionary that convers the name
     * to the ID
     */
    byName.set(item.privilege, id);
  });

  /**
   * Transform names in "includes" attribute to "id"s instead of "name"s
   */
  Object.entries(result).forEach(([key, val]) => {
    const includes = val.includes
      /**
       * filter invalid privileges and populate parents attribute
       */
      .map((name) => {
        const id = byName.get(name);

        if (!id) {
          return '';
        }

        result[id].parents = uniquePush(result[id].parents, id);

        return id;
      })
      .filter(Boolean) as string[];

    result[key] = {
      ...val,
      includes,
    };
  });

  /**
   * Remove cyclic dependencies
   */
  const sorted = sort(result);
  const visited: Set<string> = new Set();

  function visit(item: TSanitizedPrivilege) {
    visited.add(item.id);
    item.includes = item.includes.filter((k) => !visited.has(k));
  }

  sorted.forEach(visit);

  /**
   * Recalculate the parents attribute
   */
  sorted.forEach((item) => (item.parents = []));
  sorted.forEach((item) => {
    item.includes.forEach((key) => result[key].parents.push(item.id));
  });

  /**
   * Add Guest privilege is it doesn't exist
   */

  const found = Object.values(result).find(
    ({ privilege }) => privilege === GUEST,
  );

  if (found) return result;

  const id = generate();

  return {
    [id]: {
      id,
      privilege: GUEST,
      includes: [],
      parents: [],
      resources: [],
    },
    ...result,
  };
}

/**
 * sort list of sanitized pivileges
 * @param dict sanitized data
 * @returns sorted privileges
 */
export function sort(dict: TById): TSanitizedPrivilege[] {
  return Object.values(dict).sort(
    (a, b) => a.parents.length - b.parents.length,
  );
}

/**
 * Lists the parents of a specific element
 * @param id identifier of the current element
 * @param dict sanitized privileges
 * @returns parents of the current element
 */
export function parents(id: string, dict: TById) {
  const item = dict[id];
  let result: string[] = [];

  if (!item) {
    return result;
  }

  item.parents.forEach((key) => {
    result = uniquePush(result, ...parents(key, dict), key);
  });

  return result;
}

/**
 * Lists the children of a specific element
 * @param id identifier of the current element
 * @param dict sanitized privileges
 * @returns children of the current element
 */
export function children(id: string, dict: TById) {
  const item = dict[id];
  let result: string[] = [];

  if (!item) {
    return result;
  }

  item.includes.forEach((key) => {
    result = uniquePush(result, ...children(key, dict), key);
  });

  return result;
}

/**
 * Calculates from a sanitized privileges dictionary the list of allowed to include children
 * @param id identifier of the current element
 * @param dict sanitized privileges
 * @returns allowed to include privileges
 */
export function allowed(id: string, dict: TById) {
  const item = dict[id];

  if (!item) {
    return [];
  }

  const exclude = [id, ...parents(id, dict), ...item.includes];

  return Object.keys(dict).filter((k) => !exclude.includes(k));
}

/**
 * Removes an element from a sanitized dictionary
 * @param id identifier of the current element
 * @param dict sanitized privileges
 * @returns new dictionary containing the initialone except the one with "id"
 */
export function remove(id: string, dict: TById): TById {
  const item = dict[id];

  if (!item) {
    return dict;
  }

  return Object.keys(dict).reduce((prev, curr) => {
    if (curr === id) {
      return prev;
    }

    return {
      ...prev,
      [curr]: {
        ...dict[curr],
        parents: dict[curr].parents.filter((p) => p !== id),
        includes: dict[curr].includes.filter((p) => p !== id),
      },
    };
  }, {});
}

/**
 * Adds an existing node as a child of another node
 * @param parentId the parent identifier
 * @param childId the child identifer
 * @param dict sanitized privileges dictionary
 * @returns the new dictionary
 */
export function addIn(parentId: string, childId: string, dict: TById): TById {
  const parent = dict[parentId];
  const child = dict[childId];

  if (!parent || !child) {
    return dict;
  }

  return {
    ...dict,
    [parentId]: {
      ...dict[parentId],
      includes: uniquePush(parent.includes, childId),
    },
    [childId]: {
      ...dict[childId],
      parents: uniquePush(child.parents, parentId),
    },
  };
}

/**
 * Removes an existing node from its (specified) parent
 * @param parentId the parent identifier
 * @param childId the child identifer
 * @param dict sanitized privileges dictionary
 * @returns the new dictionary
 */
export function removeFrom(
  parentId: string,
  childId: string,
  dict: TById,
): TById {
  const parent = dict[parentId];
  const child = dict[childId];

  if (!parent || !child) {
    return dict;
  }

  return {
    ...dict,
    [parentId]: {
      ...dict[parentId],
      includes: dict[parentId].includes.filter((id) => id !== childId),
    },
    [childId]: {
      ...dict[childId],
      parents: dict[childId].parents.filter((id) => id !== parentId),
    },
  };
}

/**
 * Create a new privilege
 * @param name the privilege name
 * @param dict sanitized privileges dictionary
 * @returns the new dictionary
 */
export function create(name: string, dict: TById): TById {
  validateName(name);

  Object.entries(dict).forEach(([, { privilege }]) => {
    if (name.toLowerCase() === privilege.toLowerCase()) {
      throw new Error(`There's already a Privilege with the name ${name}`);
    }
  });

  const id = generate();
  return {
    [id]: {
      id,
      privilege: name,
      includes: [],
      parents: [],
      resources: [],
    },
    ...dict,
  };
}

export function createWithResource(
  name: string,
  resource: string,
  type: EPermissionType,
  action: IResourcePermission['name'],
  dict: TById,
) {
  let newDict = create(name, dict);

  const priv = Object.entries(newDict).find(
    ([, priv]) => priv.privilege === name,
  );

  if (!priv) return dict;

  newDict = togglePrivilegePermission(
    priv[1].id,
    resource,
    type,
    action,
    newDict,
  );
  return newDict;
}

/**
 * Renames an existing node
 * @param name the new name
 * @param id the node identifier
 * @param dict sanitized privileges dictionary
 * @returns the new dictionary
 */
export function rename(name: string, id: string, dict: TById): TById {
  if (!name) {
    throw new Error('Please enter a name');
  }

  if (!dict[id]) {
    throw new Error('Invalid privilege');
  }

  Object.entries(dict).forEach(([, { privilege, id: pId }]) => {
    if (name.toLowerCase() === privilege.toLowerCase() && id !== pId) {
      throw new Error(`There's already a Privilege with the name ${name}`);
    }
  });

  return {
    ...dict,
    [id]: {
      ...dict[id],
      privilege: name,
    },
  };
}

/**
 * Populates a list privileges IDs
 * @param ids the identifiers of privileges to populate
 * @param dict sanitized privileges dictionary
 * @returns populated privileges
 */
export function populateShallow(
  ids: string[],
  dict: TById,
): (IPrivilege | null)[] {
  return ids.map((id) => getPrivilegeById(id, dict));
}

/**
 * Get the privilege object from its identifier
 * @param id the identifier of the node
 * @param dict sanitized privileges dictionary
 * @returns populated privilege
 */
export function getPrivilegeById(
  id: string,
  dict: TById,
): TSanitizedPrivilege | null {
  if (dict[id]) {
    return dict[id];
  }

  return null;
}

/**
 * Maps the privileges to their permissions
 * @param param0 the raw permission object
 * @returns an object mapping the privilege to its permission
 */
export function actionsToPrivs(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  { applyTo, type, ...actions }: IPermission,
): {
  [privName: string]: string[];
} {
  return Object.entries(actions).reduce<{
    [privName: string]: string[];
  }>((previous, [key, value]) => {
    const found = _find(PERMISSIONS, ['key', key]);

    if (!found) {
      return previous;
    }

    if (!found.canApplyTo.includes(type)) {
      return previous;
    }

    const list = typeof value === 'string' ? value.split(',') : value;

    list.forEach((name: string) => {
      previous[name] = uniquePush(previous[name] || [], key);
    });

    return previous;
  }, {});
}
const generateInfoMessage = (label: TAction, isDatastore = false): any => {
  if (['read', 'create', 'update', 'delete'].includes(label.toLowerCase())) {
    return `All ${
      isDatastore ? 'dataclasses and ' : ''
    }attributes have the '${label.toLowerCase()}' permission`;
  }

  if (label.toLowerCase() === 'execute') {
    return `All functions defined ${
      isDatastore
        ? 'through the model (DataStore, Dataclasses, Entity selections, Entities) have the execute permission'
        : 'on this Dataclass, Entity selection class, Entity class have the execute permission'
    }`;
  }

  return `All ${
    isDatastore ? 'dataclasses, ' : ''
  }attributes and functions have the '${label.toLowerCase()}' permission`;
};
/**
 * Converts a raw permission to a resource
 * @param item a permission
 * @returns the resource object
 */
export function getResourceFromType(item: IPermission): IResource {
  return {
    permissions: PERMISSIONS.filter(({ canApplyTo }) =>
      canApplyTo.includes(item.type),
    ).map<IResourcePermission>(({ key, label }) => {
      const result: IResourcePermission = { name: key, from: [] };

      switch (item.type) {
        case EPermissionType.DATASTORE:
          return {
            ...result,
            info: generateInfoMessage(label as TAction, true),
          };
        case EPermissionType.DATACLASS:
          return {
            ...result,
            info: generateInfoMessage(label as TAction),
          };
        default:
          return result;
      }
    }),
    resource: item.applyTo,
    type: item.type,
  };
}

/**
 * Sanitizes the raw permissions
 * @param data list of raw permissions
 * @param byKey dictionary
 * @returns a new dictionary containing the sanitized permissions
 */
export function sanitizePermissions(data: IPermission[], byKey: TById): TById {
  const byName = getDictByName(byKey);
  const result: TById = {};

  data
    .filter(({ type }) => TYPES.includes(type))
    .forEach((item) => {
      const byPrivs = actionsToPrivs(item);
      Object.entries(byPrivs).forEach(([privName, actions]) => {
        const priv = byName[privName]?.id;

        if (!priv) {
          return;
        }

        result[priv] = result[priv] || { ...byKey[priv] };

        let found = _find(result[priv].resources, {
          resource: item.applyTo,
          type: item.type,
        });

        if (!found) {
          found = getResourceFromType(item);

          result[priv].resources.push(found);
        }

        found.permissions = found.permissions.map((permission) => ({
          ...permission,
          checked: permission.checked || actions.includes(permission.name),
        }));
      });
    });

  return {
    ...byKey,
    ...result,
  };
}

/**
 * Converts a dictionary indexed by id to a new one indexed by name
 * @param dict dictioanry
 * @returns a new dictionary indexed by privilege name
 */
export function getDictByName(dict: TById): TById {
  return Object.values(dict).reduce(
    (prev, val) => ({
      ...prev,
      [val.privilege]: val,
    }),
    {},
  );
}

/**
 * Sanitizes the content of roles.json file
 * @param data raw data
 * @returns sanitized data
 */
export function sanitize(data: TRawData): ISanitized {
  const dict = sanitizePrivileges(data.privileges || []);

  return {
    forceLogin: data?.forceLogin || false,
    privileges: sanitizePermissions(data.permissions?.allowed || [], dict),
    roles: (data.roles || [])
      .map(({ id = generate(), role, privileges = [] }) => ({
        id,
        role,
        privileges: uniquePush(privileges)
          .map(
            (privilege) =>
              Object.values(dict).find(
                ({ privilege: name }) => name === privilege,
              )?.id,
          )
          .filter(Boolean),
      }))
      .reduce((prev, curr) => {
        return {
          ...prev,
          [curr.id]: {
            ...curr,
          },
        };
      }, {}),
  };
}

/**
 * Converts sanitized data to a valid API payload
 * @param data sanitized dictionary
 * @returns raw data
 */
export function toAPI(data: ISanitized): TRawData {
  const leafs = Object.values(data.privileges)
    .filter(({ includes }) => includes.length === 0)
    .map(({ id }) => id);

  const sorted = [
    ...leafs,
    ...leafs.map((id) => parents(id, data.privileges).reverse()),
  ]
    .flat()
    .filter((id, index, arr) => arr.indexOf(id) === index)
    .map((id) => data.privileges[id]);

  return {
    forceLogin: data.forceLogin || false,
    permissions: {
      allowed: Object.values(data.privileges).reduce<IPermission[]>(
        (prev, curr) => {
          curr.resources.forEach(({ permissions, resource, type }) => {
            let found = _find(prev, {
              applyTo: resource,
              type: TypeNamesMapping(type),
            });

            if (!found) {
              found = {
                applyTo: resource,
                type: TypeNamesMapping(type),
              };

              prev.push(found);
            }

            permissions.forEach(({ name, checked }) => {
              if (checked && found) {
                found[name] = found[name] || [];
                (found[name] as string[]).push(curr.privilege);
              }
            });
          });

          return prev;
        },
        [],
      ),
    },
    privileges: sorted
      .filter(({ privilege }) => privilege !== GUEST)
      .map(({ id, includes, privilege }) => ({
        id,
        privilege,
        includes: includes.map((pId) => data.privileges[pId].privilege),
      })),
    roles: Object.values(data.roles).map((role) => ({
      ...role,
      privileges: role.privileges
        .map((privId) => {
          return data.privileges[privId]?.privilege;
        })
        .filter(Boolean),
    })),
  };
}

export function getGraphData(id: string, dict: TById): IGraphData {
  const selected = dict[id];
  const result: IGraphData = {
    children: [],
    links: [],
    parents: [],
    selected: id,
  };

  if (!selected) {
    return result;
  }

  function get(
    nodeIds: string[],
    type: 'includes' | 'parents',
    result: string[][] = [],
  ): string[][] {
    const direct = nodeIds.reduce<string[]>(
      (prev, curr) => [...prev, ...dict[curr][type]],
      [],
    );

    if (direct.length === 0) {
      return result;
    }

    result.push(direct);
    return get(direct, type, result);
  }

  function sanitizeResult(data: string[][]): typeof data {
    const visited: string[] = [];
    const result = [];

    for (let i = data.length - 1; i >= 0; i -= 1) {
      const curr = data[i].filter((nodeId) => {
        const isVisited = visited.includes(nodeId);
        if (isVisited) {
          return false;
        }

        visited.push(nodeId);
        return true;
      });

      if (curr.length > 0) {
        result.unshift(uniquePush(curr));
      }
    }

    return result;
  }

  function getLinks(
    sanitized: string[][],
    type: 'includes' | 'parents',
  ): ILink[] {
    const visited: string[] = [];
    return sanitized.reduce<ILink[]>((prev, curr) => {
      curr.forEach((nodeId) => {
        const linked =
          dict[nodeId][type === 'parents' ? 'includes' : 'parents'];

        linked.forEach((nId) => {
          if (visited.includes(nId)) {
            prev.push(
              type === 'parents'
                ? {
                    source: nodeId,
                    target: nId,
                  }
                : {
                    source: nId,
                    target: nodeId,
                  },
            );
          }
        });

        visited.push(nodeId);
      });

      return prev;
    }, []);
  }

  result.children = get([id], 'includes');
  result.children = sanitizeResult(result.children);

  result.parents = get([id], 'parents');
  result.parents = sanitizeResult(result.parents);

  result.links = [
    ...getLinks([[id], ...result.parents], 'parents'),
    ...getLinks([[id], ...result.children], 'includes'),
  ];

  return result;
}

/**
 *
 * @param id privilege id
 * @param permission
 * @param dict
 */
export function addPermission(
  id: string,
  permission: IPermission,
  dict: TById,
): TById {
  const privilege = getPrivilegeById(id, dict);

  if (!privilege) return dict;

  if (
    _find(privilege.resources, {
      resource: permission.applyTo,
      type: permission.type,
    })
  )
    return dict;

  const newPrivilege: TSanitizedPrivilege = {
    ...privilege,
    resources: [...privilege.resources, getResourceFromType(permission)],
  };

  return {
    ...dict,
    [id]: newPrivilege,
  };
}

/**
 *
 * @param id
 * @param permission
 * @param dict
 */
export function removePermission(
  id: string,
  permission: IPermission,
  dict: TById,
): TById {
  const privilege = getPrivilegeById(id, dict);

  if (!privilege) return dict;

  const newPrivilege: TSanitizedPrivilege = {
    ...privilege,
    resources: privilege.resources.filter(
      ({ resource, type }) =>
        resource !== permission.applyTo || type !== permission.type,
    ),
  };

  return {
    ...dict,
    [id]: newPrivilege,
  };
}

/**
 * @param resourcesByPermission
 * @param dict
 */
export function removeMultiplePermissions(
  resourcesByPermission: IResourcesByPermission,
  dict: TById,
): TById {
  const newResouces: TById = Object.entries(resourcesByPermission)
    .map(([id, permissions]) => {
      const privilege = getPrivilegeById(id, dict);

      if (!privilege) return { [id]: dict[id] };

      const newPrivilege: TSanitizedPrivilege = {
        ...privilege,
        resources: privilege.resources.filter(
          ({ resource, type }) =>
            !permissions.some(
              (perm) => perm.applyTo === resource && perm.type === type,
            ),
        ),
      };

      return {
        [id]: newPrivilege,
      };
    })
    .reduce((prev, curr) => ({ ...prev, ...curr }), {});

  return {
    ...dict,
    ...newResouces,
  };
}

/**
 *
 * @param id
 * @param permission
 * @param dict
 */
export function resetResources(id: string, dict: TById): TById {
  const privilege = getPrivilegeById(id, dict);

  if (!privilege) return dict;

  const newPrivilege: TSanitizedPrivilege = {
    ...privilege,
    resources: privilege.resources
      .filter((r) => r.permissions.some((item) => item.from.length))
      .map((item) => ({
        ...item,
        permissions: item.permissions.map((t) => ({ ...t, checked: false })),
      })),
  };

  return {
    ...dict,
    [id]: newPrivilege,
  };
}
/**
 *
 * @param id privilege id
 * @param resource resource name
 * @param type resource type
 * @param permission permission name
 * @param dict privileges dictionary
 * @returns
 */
export function togglePrivilegePermission(
  id: string,
  resource: string,
  type: EPermissionType,
  permission: IResourcePermission['name'],
  dict: TById,
): TById {
  const privilege = getPrivilegeById(id, dict);

  if (!privilege) return dict;

  const found = _find(privilege.resources, {
    type,
    resource,
  });

  const newPrivilege: TSanitizedPrivilege = found
    ? {
        ...privilege,
        resources: privilege.resources.map((ress) => {
          if (ress.type === type && ress.resource === resource) {
            return {
              ...ress,
              permissions: ress.permissions.map((perm) => {
                if (perm.name === permission) {
                  return {
                    ...perm,
                    checked: !perm.checked,
                    from: [],
                  };
                }

                return perm;
              }),
            };
          }

          return ress;
        }),
      }
    : {
        ...privilege,
        resources: [
          ...privilege.resources,
          {
            type,
            resource,
            permissions: PERMISSIONS.filter((perm) =>
              perm.canApplyTo.includes(type),
            ).map((perm) => ({
              from: [],
              name: perm.key,
              checked: perm.key === permission,
              info: generateInfoMessage(
                perm.key,
                type === EPermissionType.DATASTORE,
              ),
            })),
          },
        ],
      };

  return {
    ...dict,
    [id]: newPrivilege,
  };
}

/**
 *
 * @param id
 * @param permission
 * @param dict
 */
export function togglePermission(
  id: string,
  resource: IResource,
  permission: IResourcePermission,
  dict: TById,
): TById {
  const privilege = getPrivilegeById(id, dict);

  if (!privilege) return dict;

  const found = _find(privilege.resources, {
    type: resource.type,
    resource: resource.resource,
  });

  const newPrivilege: TSanitizedPrivilege = found
    ? {
        ...privilege,
        resources: privilege.resources.map((ress) => {
          if (
            ress.type === resource.type &&
            ress.resource === resource.resource
          ) {
            return {
              ...ress,
              permissions: resource.permissions.map((perm) => {
                if (perm.name === permission.name) {
                  return {
                    ...perm,
                    checked: !perm.checked,
                    from: [],
                  };
                }

                return perm;
              }),
            };
          }

          return ress;
        }),
      }
    : {
        ...privilege,
        resources: [
          ...privilege.resources,
          {
            ...resource,
            permissions: resource.permissions.map((perm) => ({
              ...perm,
              checked: permission.name === perm.name,
              from: [],
            })),
          },
        ],
      };

  return {
    ...dict,
    [id]: newPrivilege,
  };
}

export function getPromotions(id: string, dict: TById) {
  const inheretedResources = getInheritedResources(id, dict) || [];

  const mainResources = getPrivilegeById(id, dict)?.resources || [];

  // asigning the crown for both own and inhereted resources
  const resources = [...inheretedResources, ...mainResources];

  const privMethods =
    resources.filter(({ type }) =>
      [
        EPermissionType.DS_METHOD,
        EPermissionType.DATACLASS_METHOD,
        EPermissionType.ENTITY_METHOD,
        EPermissionType.ENTITY_SEL_METHOD,
      ].includes(type),
    ) || [];

  const names = privMethods.map(({ resource }) => resource);
  const result = Object.entries(dict).reduce<Record<string, string[]>>(
    (prev, [id, { resources }]) => {
      resources
        .filter(
          ({ resource, type }) =>
            [
              EPermissionType.DS_METHOD,
              EPermissionType.DATACLASS_METHOD,
              EPermissionType.ENTITY_METHOD,
              EPermissionType.ENTITY_SEL_METHOD,
            ].includes(type) && names.includes(resource),
        )
        .forEach(({ permissions, resource }) => {
          const isPromoted = permissions.find(
            ({ name }) => name === 'promote',
          )?.checked;
          if (isPromoted) {
            prev[resource] = uniquePush(prev[resource] || [], id);
          }
        });

      return prev;
    },
    {},
  );
  return result;
}

export function getInheritedResources(id: string, dict: TById): IResource[] {
  const privilege = getPrivilegeById(id, dict);

  if (!privilege) return [];

  const guestId = Object.values(dict).find(
    (priv) => priv.privilege === GUEST,
  )?.id;

  const childrenIds =
    guestId && guestId !== id
      ? uniquePush(children(id, dict), guestId)
      : children(id, dict);

  const result = childrenIds.reduce<IResource[]>(
    (prev, curr) => {
      const obj = dict[curr];
      obj.resources.forEach((resource) => {
        const checked = resource.permissions.filter(
          ({ checked, name }) => checked && name !== 'promote',
        );

        if (checked.length === 0) {
          return;
        }

        const found = _find(prev, {
          type: resource.type,
          resource: resource.resource,
        });

        if (!found) {
          prev.push({
            ...resource,
            permissions: resource.permissions.map((perm) => ({
              ...perm,
              from: perm.checked ? [curr] : [],
              checked: false,
            })),
          });

          return;
        }

        found.permissions = found.permissions.map((perm, index) => {
          let { from = [] } = perm;
          if (resource.permissions[index].checked && !from.includes(curr)) {
            from = [...from, curr];
          }
          return {
            ...perm,
            from,
            checked: perm.checked,
          };
        });
      });
      return prev;
    },
    clone(
      privilege.resources.map<IResource>((ress) => ({
        ...ress,
        permissions: ress.permissions.map(({ checked, ...perm }) => ({
          ...perm,
          checked: Boolean(checked),
        })),
      })),
    ),
  );

  return result;
}

export const toState = (rolesArray: TRole[]): TRolesDict => {
  return rolesArray.reduce((prev, curr) => {
    return {
      ...prev,
      [curr.id]: {
        ...curr,
      },
    };
  }, {});
};

export const removeRole = (id: string, roles: TRolesDict): TRolesDict => {
  if (!roles[id]) return roles;

  return Object.values(roles).reduce((prev, curr) => {
    if (id === curr.id || curr.role === '') {
      return prev;
    }

    return {
      ...prev,
      [curr.id]: curr,
    };
  }, {});
};

export const editRole = (
  id: string,
  newName: string,
  roles: TRolesDict,
): TRolesDict => {
  if (!roles[id]) return roles;

  return Object.values(roles).reduce((prev, curr) => {
    if (id === curr.id) {
      return {
        ...prev,
        [curr.id]: {
          ...curr,
          name: newName,
        },
      };
    }

    return {
      ...prev,
      [curr.id]: curr,
    };
  }, {});
};

export const createRole = (
  id: string,
  name: string,
  roles: TRolesDict,
): TRolesDict => {
  const newRole = {
    id,
    role: name,
    privileges: [],
  };

  return {
    ...roles,
    [newRole.id]: newRole,
  };
};

export const saveRole = (role: TRole, roles: TRolesDict): TRolesDict => {
  validateName(role.role);

  const found = Object.values(roles).find((item) => item.role === role.role);

  if (found) {
    throw new Error(`a role with the name ${role.role} already exists`);
  }

  return Object.values(roles).reduce((prev, curr) => {
    if (role.id === curr.id) {
      return {
        ...prev,
        [role.id]: role,
      };
    }

    return {
      ...prev,
      [curr.id]: curr,
    };
  }, {});
};
